// src/pages/Community/StoryCharacters/CharacterDetail.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./css/CharacterDetail.css";
import CharacterDetailContent from "./CharacterDetailContent";
import {
  LikeBar,
  ReactionBar,
  Bookmark,
  PresenceBadge,
  Discussion,
} from "../components";
import FeedbackModal from "../components/FeedbackModal"; // <-- added

/* ---------- Helpers (must be defined before use) ---------- */

/**
 * Convert a YouTube url (youtu.be or youtube.com/...v=) into an embed URL.
 * Returns null when input doesn't look like a YouTube address.
 */
function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

/**
 * Normalize Firestore document shape into the shape expected by the UI.
 * Keeps defensive checks so documents with a few missing fields don't crash the UI.
 */
export function normalizeCharacterData(d = {}) {
  const clean = (v) => (typeof v === "string" ? v.trim() : v);

  // tags
  const tags = Array.isArray(d.tags)
    ? d.tags
    : d.tags
      ? String(d.tags)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      : [];

  const gallery = Array.isArray(d.gallery) ? d.gallery : [];

  // extract nested bio object
  const bioRaw = d.bio && typeof d.bio === "object" ? d.bio : {};
  // ✅ DEFINE FIRST
  const affiliations =
    Array.isArray(bioRaw.affiliations)
      ? bioRaw.affiliations
      : Array.isArray(d.affiliations)
        ? d.affiliations
        : [];
  const bio = {
    fullName: clean(bioRaw.fullName) || null,
    born: clean(bioRaw.born) || null,
    background: clean(bioRaw.background) || null,
    personality: clean(bioRaw.personality) || null,
    skills: clean(bioRaw.skills) || null,
    dreams: clean(bioRaw.dreams) || null,

    // FIX HERE
    liking: clean(bioRaw.liking ?? bioRaw.likes) || null,

    partner: clean(bioRaw.partner) || null,
    children: clean(bioRaw.children) || null,
    status: clean(bioRaw.status) || null,
    affiliations,
  };

  return {
    name: d.characterName ?? bio.fullName ?? d.name ?? "Unnamed",
    portrait: d.imageUrl ?? d.portrait ?? null,
    authorUid: d.authorUid ?? d.authorId ?? d.createdBy ?? d.userId ?? null,

    role: d.role ?? d.roleAtCreation ?? "Character",
    characterType: d.characterType ?? null,
    firstAppearance: d.firstAppearance ?? null,
    affiliation: d.affiliation ?? d.game ?? null,

    desc: clean(d.synopsis) || clean(d.story) || null,
    synopsis: clean(d.synopsis) || null,
    storyLong: clean(d.story) || null,

    backstory: clean(d.backstory) || null,
    arc: clean(d.arc) || null,
    fate: clean(d.fate) || null,

    quotes: Array.isArray(d.quotes) ? d.quotes : [],
    relationships: Array.isArray(d.relationships) ? d.relationships : [],
    gallery,
    videoUrl: d.videoUrl ?? null,

    tags,
    voiceActor: bioRaw.voiceActor ?? null,
    age: typeof d.age === "number" ? d.age : d.age ? Number(d.age) : null,
    origin: d.origin ?? null,
    location: d.location ?? null,
    occupation: d.occupation ?? null,
    alias: d.alias ?? null,

    bio, // ✅ affiliations live here
  };
}


/**
 * Generic Section component used throughout the page.
 * Kept as a function declaration (not arrow) so it's hoisted and available to linting.
 */
function Section({ id, title, children }) {
  const headingId = title ? `${id}-h` : undefined;
  return (
    <section
      id={id}
      className="char-sec"
      {...(title ? { "aria-labelledby": headingId } : { "aria-label": id })}
    >
      {title && (
        <h2 className="char-h2" id={headingId}>
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/* ---------- Component ---------- */

export default function CharacterDetail() {
  const { id } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const game = params.get("game") || "gta-6";

  const [c, setC] = useState(undefined); // undefined = loading, null = not found
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  // Feedback modal state
  const [fbOpen, setFbOpen] = useState(false); // <-- added

  // Lightbox
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const lightboxRef = useRef(null);

  const basePath = useMemo(() => ["characters", id], [id]);
  const bookmarkKey = useMemo(() => `characters:${id}`, [id]);

  /* ----- Data ----- */
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "characters", id), (snap) => {
      if (!snap.exists()) {
        setC(null);
        return;
      }
      const raw = snap.data();
      const normalized = normalizeCharacterData(raw);
      setC({ id: snap.id, ...normalized, _raw: raw });
    });
    return () => unsub();
  }, [id]);

  const yt = useMemo(
    () => (c?.videoUrl ? toYouTubeEmbed(c.videoUrl) : null),
    [c?.videoUrl]
  );

  /* ----- Share/Copy ----- */
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) {
        await navigator.share({ title: c?.name ?? "Character", url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch {
      // no-op
    }
  }, [c?.name, location.pathname, location.hash]);

  /* ----- Lightbox ----- */
  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });

  const nextLight = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightbox((s) => {
        const len = c?.gallery?.length ?? 0;
        return { open: true, index: len ? (s.index + 1) % len : 0 };
      });
    },
    [c?.gallery?.length]
  );

  const prevLight = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightbox((s) => {
        const len = c?.gallery?.length ?? 0;
        return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
      });
    },
    [c?.gallery?.length]
  );

  useEffect(() => {
    if (!lightbox.open) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, nextLight, prevLight]);

  // Scroll to anchor if URL has hash
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, c]);

  const toc = useMemo(() => {
    if (!c) return [];
    const items = [];
    if (c.desc?.trim?.()) items.push({ id: "bio", label: "Description" });
    if (
      c.bio?.background ||
      c.bio?.personality ||
      c.bio?.skills ||
      c.bio?.dreams ||
      c.bio?.liking
    ) {
      items.push({ id: "biography", label: "Biography" });
    }
    if (
      c.storyLong?.trim?.() ||
      c.backstory?.trim?.() ||
      c.arc?.trim?.() ||
      c.fate?.trim?.()
    )
      items.push({ id: "story", label: "Story" });
    if (Array.isArray(c.quotes) && c.quotes.length > 0)
      items.push({ id: "quotes", label: "Notable Quotes" });
    if (Array.isArray(c.relationships) && c.relationships.length > 0)
      items.push({ id: "relationships", label: "Relationships" });
    if (Array.isArray(c.gallery) && c.gallery.length > 0)
      items.push({ id: "gallery", label: "Gallery" });
    if (c.videoUrl) items.push({ id: "video", label: "Video" });
    return items;
  }, [c]);

  /* ----- Loading / 404 ----- */
  if (c === undefined) {
    return (
      <div className="char-wrap">
        <div className="char-skel-card" aria-busy="true">
          <div className="char-skel-hero" />
          <div className="char-skel-title" />
          <div className="char-skel-row" />
          <div className="char-skel-section" />
        </div>
      </div>
    );
  }

  if (c === null) {
    return (
      <div className="char-wrap">
        <div className="char-empty-card" role="alert">
          <div className="char-empty-title">We can’t find that character</div>
          <p className="char-empty-sub">
            It may have been removed or the link is incorrect.
          </p>
          <Link
            to="/story-characters"
            className="char-btn char-btn--primary"
          >
            ← Back to Story & Characters
          </Link>
        </div>
      </div>
    );
  }

  /* ----- Render ----- */
  return (
    <>
      <div className="char-wrap">
        {/* Top bar */}
        <div className="char-detail-toprow">
          <Link
            to={`/story-characters?game=${game}`}
            className="char-back"
            aria-label="Back"
          >
            ← All characters
          </Link>


          <div className="char-actions">
            {c.videoUrl && !yt && (
              <a
                className="char-action"
                href={c.videoUrl}
                target="_blank"
                rel="noreferrer"
              >
                ▶ Open video
              </a>
            )}
            <button className="char-action" onClick={onShare}>
              ⤴ Share
            </button>

            {/* Feedback button (added) */}
            <button
              className="char-action"
              onClick={() => setFbOpen(true)}
              aria-label="Give feedback"
              title="Give feedback"
            >
              💬 Feedback
            </button>
          </div>
        </div>

        <CharacterDetailContent
          c={c}
          showTitle
          basePath={["characters", c.id]}
          selectedReaction={null}
          onSelectReaction={() => { }}
          onOpenLightbox={() => { }}
          showDiscussion={true}
        />


      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(c.gallery) && c.gallery.length > 0 && (
        <div
          ref={lightboxRef}
          className="char-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <button
            className="char-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            className="char-lightbox-nav left"
            onClick={prevLight}
            aria-label="Previous"
          >
            ‹
          </button>
          <img
            className="char-lightbox-img"
            src={c.gallery[lightbox.index]}
            alt={`Image ${lightbox.index + 1}`}
          />
          <button
            className="char-lightbox-nav right"
            onClick={nextLight}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      )}

      {/* Feedback modal (added) */}
      <FeedbackModal
        open={fbOpen}
        onClose={() => setFbOpen(false)}
        character={c}
        sourceComponent="CharacterDetail"
        source={location.pathname}
      />
    </>
  );
}
